import { insidePolygon } from '../domain/geometry.js';
import { CONTENT } from '../content/runtime.js';
import { at } from '../content/placement.js';
import { seatRowCellSize, checkInDeskCellSize, checkInWingCellSize, terminalPropCellSizes } from './jet-set-frames.js';
import { Container, Graphics, Matrix, type LINE_CAP, type LINE_JOIN } from "pixi.js";
import { CONFIG, ADMIN, ADMIN_DESK, PLANTERS, TERMINAL_PROPS, TROLLEYS, STATION_FOOTPRINTS, CHECK_IN_DESKS, SOFAS, seatFacing, BOUNDARIES, DIVIDER, BINS, binPad, GATES, TOWER_ART, ENTRANCE, LANDSIDE_TOP, bounds } from "../domain/config.js";
import { C, shape, rounded, label } from "./primitives.js";
import { detail, prop, groundedProp, groundedDetail, propHitArea, faceAircraft, primitive } from "./sprites.js";
import type { Point } from "../domain/types.js";
import { CAPTAIN_CELL, PILOT_CELLS, STAFF_CELL, passengerCell, personSprite } from './people.js';
import { PLANE_SIZE, RUNWAY_Y, RUNWAY, APRON, taxiRoute, aircraftDoor, jetBridge, BRIDGE_HEAD, boardingStyle, standReach } from '../domain/gateflow.js';
import { activeAirport } from '../domain/active-layout.js';
import { gateLayout } from '../domain/layout.js';
import { projectDirection, projectWorld, standAt, unprojectWorld } from './camera.js';
export const SHIRTS = [0xf0867d, 0x719bd0, 0xd5b069, 0x997ccb, 0x66b69b, 0xe4abca];
export function person(color: number, captain = false, staff = false, variant = 0, role = '') {
    const c = new Container();
    const body = new Container(), feet = new Container();
    c.addChild(body);
    const passengerIndex = Math.max(0, SHIRTS.indexOf(color));
    const cell = captain ? CAPTAIN_CELL : role === 'pilot' ? PILOT_CELLS[variant % PILOT_CELLS.length] : staff ? STAFF_CELL : passengerCell(passengerIndex * 2 + variant % 2);
    const image = personSprite(cell);
    body.addChild(image);
    body.addChild(feet);
    return { container: c, body, feet, image, cell };
}
/** An upright prop standing on a world floor point; the scene projects it for the current camera. */
export type Standing = { node: Container; x: number; y: number; depth: number };
function stand(props: Standing[], node: Container, x: number, y: number, depth = 0) { props.push({ node, x, y, depth }); return node; }
/** Squares in world space become isometric diamonds once the floor is projected. */
function checker(g: Graphics, left: number, top: number, right: number, bottom: number, size: number, color: (x: number, y: number) => number, alpha: number) {
    for (let y = top, row = 0; y < bottom; y += size, row++)
        for (let x = left + (row % 2) * size; x < right; x += size * 2)
            g.beginFill(color(x, y), alpha).drawRect(x, y, Math.min(size, right - x), Math.min(size, bottom - y)).endFill();
}
/** Flat floor art in world coordinates. The scene lays it on an isometric transform; everything that
 *  stands up (props, signs) is returned separately so it can be projected and depth-sorted. */
export function floor(visitBin?: (point: Point) => void): { ground: Container; airfield: Container; props: Standing[]; gates: Container; inside: Container } {
    const c = new Container(), props: Standing[] = [], airfield = new Container();
    c.zIndex = -10000;
    c.addChild(airfield);
    const W = CONFIG.width, H = CONFIG.height;
    // The open ground and airfield are one layer, which drops a storey below a terminal with an upper departures deck.
    shape(airfield).beginFill(0x55c9c2).drawRect(-4000, -4000, W + 8000, H + 8000).endFill();
    // Airfield: runway, then the apron where aircraft park at the jet bridges.
    const field = shape(airfield);
    const R = RUNWAY, length = R.right - R.left;
    field.beginFill(0x273f70).drawRect(R.left, R.top, length, R.bottom - R.top).endFill();
    // Edge lines, threshold bars and runway numbers at each end, and the dashed centreline between them.
    field.beginFill(0xf4f8f0, 0.75).drawRect(R.left + 20, R.top + 14, length - 40, 5).drawRect(R.left + 20, R.bottom - 19, length - 40, 5).endFill();
    for (const [end, dir] of [[R.left, 1], [R.right, -1]] as const) {
        for (let bar = 0; bar < 8; bar++) field.beginFill(0xf4f8f0, 0.85).drawRect(end + dir * 40 - (dir < 0 ? 70 : 0), R.top + 36 + bar * 22, 70, 12).endFill();
        const number = label(airfield, dir > 0 ? '09' : '27', end + dir * 160, RUNWAY_Y, 54, 0xf4f8f0, '900');
        number.anchor.set(0.5); number.rotation = dir * Math.PI / 2; number.alpha = 0.8;
    }
    field.lineStyle(4, 0xf4f8f0, 0.7);
    for (let x = R.left + 240; x < R.right - 300; x += 120) field.moveTo(x, RUNWAY_Y).lineTo(x + 60, RUNWAY_Y);
    field.lineStyle(0).beginFill(0x638493).drawRect(R.left, APRON.top, length, APRON.bottom - APRON.top).endFill();
    // A gate off the north wall gets a stand pad and a taxiway out to the apron, with a yellow lead line.
    const code = activeAirport(), plan = gateLayout(code).GATES as readonly { door?: Point; facing?: readonly number[] }[];
    plan.forEach((gate, i) => {
        if (!gate.door || (gate.facing?.[1] ?? -1) < 0) return;
        const route = taxiRoute(code, i).map(p => ({ x: p.x, y: Math.max(APRON.top + 20, p.y) })), stand = route[0];
        field.beginFill(0x638493).drawRoundedRect(stand.x - 160, stand.y - 160, 320, 320, 40).endFill();
        field.lineStyle({ width: 120, color: 0x638493, join: 'round' as LINE_JOIN, cap: 'round' as LINE_CAP }).moveTo(route[0].x, route[0].y);
        for (const p of route.slice(1)) field.lineTo(p.x, p.y);
        field.lineStyle({ width: 4, color: 0xffde72, alpha: 0.8, join: 'round' as LINE_JOIN }).moveTo(route[0].x, route[0].y);
        for (const p of route.slice(1)) field.lineTo(p.x, p.y);
        field.lineStyle(0);
    });
    field.beginFill(0xffde72).drawRect(R.left, APRON.top - 2, length, 3).endFill();
    // Curb and road outside the entrance.
    const road = shape(c);
    road.beginFill(0x5f7a8c).drawRect(24, H, W - 48, 150).endFill().beginFill(0xd8e3e6).drawRect(24, H, W - 48, 22).endFill();
    road.lineStyle(4, 0xf4f8f0, 0.7);
    for (let x = 60; x < W - 60; x += 110) road.moveTo(x, H + 90).lineTo(x + 55, H + 90);
    road.lineStyle(0);
    for (let i = 0; i < 7; i++) road.beginFill(0xf4f8f0, 0.8).drawRect(ENTRANCE.x - 100 + i * 30, H + 26, 16, 58).endFill();
    label(c, 'DROP OFF', ENTRANCE.x + 140, H + 62, 14, 0xf4f8f0, '800');
    // Everything on the terminal floor lives in one layer the scene clips to the airport's outline.
    const inside = new Container();
    c.addChild(inside);
    // Terminal floors: carpeted airside, tiled landside.
    const g = shape(inside);
    g.beginFill(0x8bb5c5).drawRect(20, 340, W - 40, H - 350).endFill();
    const zones = (CONTENT.layout as { ZONES?: { zone: string; ring: number[] }[] }).ZONES;
    if (zones) {
        // A map floor: carpet airside, tiles landside, each filled in its own shape (the scene clips to the outline).
        g.beginFill(0x8bb5c5).drawRect(-200, -200, W + 400, H + 400).endFill();
        for (const z of zones) {
            const land = z.zone === 'landside', size = land ? 72 : 88;
            g.beginFill(land ? 0xc9dfdc : 0xbcd3db).drawPolygon(z.ring).endFill();
            const xs = z.ring.filter((_, i) => i % 2 === 0), ys = z.ring.filter((_, i) => i % 2 === 1);
            for (let y = Math.min(...ys); y < Math.max(...ys); y += size) for (let x = Math.min(...xs); x < Math.max(...xs); x += size)
                if ((Math.floor(x / size) + Math.floor(y / size)) % 2 === 0 && insidePolygon(z.ring, x + size / 2, y + size / 2)) g.beginFill(0xffffff, land ? 0.11 : 0.09).drawRect(x, y, size, size).endFill();
        }
    } else {
        g.beginFill(0xbcd3db).drawRect(26, 345, W - 52, LANDSIDE_TOP - 345).endFill();
        checker(g, 26, 345, W - 26, LANDSIDE_TOP - 10, 88, () => 0xffffff, 0.09);
        g.beginFill(0xc9dfdc).drawRect(26, LANDSIDE_TOP - 10, W - 52, H - 24 - LANDSIDE_TOP + 10).endFill();
        checker(g, 26, LANDSIDE_TOP, W - 26, H - 24, 72, () => 0xffffff, 0.11);
    }
    // A broad clear concourse connects arrivals, check-in, and the security entrances.
    shape(inside).beginFill(0xf7f4e8, 0.52).drawRect(95, 1280, W - 190, 135).endFill();
    shape(inside).beginFill(0x6ab7b7, 0.34).drawRect(95, 1274, W - 190, 5).endFill();
    // Gate hold rooms follow the airport's gate row, so the scene redraws this layer when the floor plan changes.
    const gates = new Container();
    c.addChild(gates);
    c.addChild(inside); // department areas sit above the gate carpets
    drawGateFloor(gates, GATES as never, standReach(activeAirport()));
    // Department areas around each desk; divider desks only have their landside half.
    const areaFloors = [0x9fb7f8, 0xf6d493, 0x8fdac7, 0xc5b4f4, 0xf4bedf, 0xa8d8ee, 0xa5bff6, 0xf2c79a];
    // Department areas around each desk, in the desk's own frame (across the counter, out toward customers).
    const box = (s: Point & { facing?: readonly number[] }, side1: number, side2: number, out1: number, out2: number) => {
        const corners = [at(s, side1, out1), at(s, side2, out2)];
        const left = Math.min(...corners.map(p => p.x)), right = Math.max(...corners.map(p => p.x)), top = Math.min(...corners.map(p => p.y)), bottom = Math.max(...corners.map(p => p.y));
        return { left, top, w: right - left, h: bottom - top };
    };
    const mapped = (s: { facing?: readonly number[] }) => !!s.facing && (s.facing[0] !== 0 || s.facing[1] !== 1) || !!(CONFIG as { mapped?: boolean }).mapped;
    for (const [index, s] of CONFIG.stages.entries()) {
        const h = STATION_FOOTPRINTS[index].h;
        // Shared-plan desks on the security wall (y < 830) only show their landside half.
        const far = index === 6 ? 135 : s.y < 830 && !mapped(s) ? 810 - s.y : index === 1 ? 330 : 250;
        const area = index === 6 ? box(s, -260, 100, -h / 2 - 65, far) : box(s, -190, 185, -h / 2 - 65, far);
        shape(inside).lineStyle(3, areaFloors[index], 0.76).beginFill(areaFloors[index], 0.16).drawRoundedRect(area.left, area.top, area.w, area.h, 16).endFill();
        const marks = shape(inside);
        if (index === 6) {
            const line = box(s, -3 * 52 - 16, 16, h / 2 + 66 - 18, h / 2 + 66 + 18);
            marks.beginFill(0x3c92a5, 0.15).drawRoundedRect(line.left, line.top, line.w, line.h, 8).endFill();
            for (let n = 0; n < 4; n++) { const a = at(s, -n * 52, h / 2 + 66 - 12), b = at(s, -n * 52, h / 2 + 66 + 12); marks.lineStyle(2, 0x4d8799, 0.65).moveTo(a.x, a.y).lineTo(b.x, b.y); }
        } else for (let n = 0; n < 3; n++) {
            const a = at(s, -16, h / 2 + 66 + n * 40), b = at(s, 16, h / 2 + 66 + n * 40);
            marks.lineStyle(2, 0xffffff, 0.4).moveTo(a.x, a.y).lineTo(b.x, b.y);
        }
    }
    // Security and passport lanes through the divider.
    for (const s of [CONFIG.stages[2], CONFIG.stages[3]]) {
        const lanes = shape(inside);
        for (const side of [-1, 1]) { const r = box(s, side * 112, side * 192, -60, 60); lanes.beginFill(0x273c78, 0.18).drawRect(r.left, r.top, r.w, r.h).endFill(); }
        for (const side of [-1, 1]) { const a = at(s, side * 192, -60), b = at(s, side * 192, 60); lanes.lineStyle(3, 0xffde72, 0.9).moveTo(a.x, a.y).lineTo(b.x, b.y); }
    }
    // The entrance mat stops at the south wall's open doorway.
    rounded(inside, ENTRANCE.x - 90, H - 120, 180, 96, 12, 0x3eaec5);
    label(inside, 'ARRIVALS · DEPARTURES', ENTRANCE.x - 72, H - 82, 10, C.white, '700');
    if (!zones) {
        label(inside, 'AIRSIDE', 60, 760, 20, 0x5b6fb4, '800');
        label(inside, 'LANDSIDE', 60, 870, 20, 0x2889ad, '800');
    }
    for (const b of BINS) {
        const bin = new Container(), image = groundedDetail(4, 65);
        bin.addChild(image); stand(props, bin, b.x, b.y);
        if (visitBin) {
            image.eventMode = 'static'; image.cursor = 'pointer';
            image.on('pointerdown', event => { event.stopPropagation(); visitBin(binPad(b)); });
        }
    }
    for (const r of PLANTERS) stand(props, groundedProp(9, r.size), r.x, r.y);
    for (const r of SOFAS) {
        const facing = (r as { facing?: number[] }).facing;
        const turn = facing ? (facing[1] < 0 ? 2 : facing[1] > 0 ? 0 : facing[0] > 0 ? 1 : 3) : r.y < 850 ? seatFacing(r) < 0 ? 2 : 0 : 1;
        const sofa = new Container(), image = groundedProp(7, seatRowCellSize, turn);
        sofa.addChild(image); stand(props, sofa, r.x, r.y);
    }
    for (const r of TERMINAL_PROPS) {
        const item = new Container(), image = groundedProp(r.index, terminalPropCellSizes[r.index] ?? r.size, r.turn);
        item.addChild(image); stand(props, item, r.x, r.y);
    }
    for (const r of TROLLEYS) {
        const trolley = new Container(), image = groundedDetail(12, 44, 1);
        trolley.addChild(image); stand(props, trolley, r.x, r.y);
    }
    for (const { x, y } of [CHECK_IN_DESKS[0], CHECK_IN_DESKS[2], ADMIN_DESK]) {
        const desk = new Container();
        const counter = groundedProp(16, x === ADMIN_DESK.x && y === ADMIN_DESK.y ? 60 : checkInWingCellSize, 2);
        desk.addChild(counter);
        if (x === ADMIN_DESK.x && y === ADMIN_DESK.y && visitBin) {
            counter.eventMode = 'static'; counter.cursor = 'pointer';
            counter.on('pointerdown', event => { event.stopPropagation(); visitBin(ADMIN); });
        }
        stand(props, desk, x, y);
    }
    // The control tower stands on the airfield, not in the terminal.
    stand(props, prop(13, 270), TOWER_ART.x, TOWER_ART.y);
    const tower = label(new Container(), 'CONTROL TOWER', 0, -250, 16, C.white, '800');
    tower.anchor.set(0.5, 0);
    stand(props, tower.parent!, TOWER_ART.x, TOWER_ART.y, 1);
    return { ground: c, airfield, props, gates, inside };
}
type Box = { left: number; top: number; right: number; bottom: number };
/** Extrudes a world rectangle into an isometric box in screen space. Long boxes are cut into short
 *  pieces so each piece depth-sorts against the people walking beside it. */
function box(out: Container[], r: Box, height: number, top: number, light: number, dark: number, sort: 'near' | 'far' | 'center' = 'center', alpha = 1) {
    const along = r.right - r.left >= r.bottom - r.top, length = along ? r.right - r.left : r.bottom - r.top;
    const pieces = Math.max(1, Math.ceil(length / 48));
    for (let n = 0; n < pieces; n++) {
        const a = n / pieces, b = (n + 1) / pieces;
        const piece = along ? { ...r, left: r.left + (r.right - r.left) * a, right: r.left + (r.right - r.left) * b } : { ...r, top: r.top + (r.bottom - r.top) * a, bottom: r.top + (r.bottom - r.top) * b };
        prism(out, [{ x: piece.left, y: piece.top }, { x: piece.right, y: piece.top }, { x: piece.right, y: piece.bottom }, { x: piece.left, y: piece.bottom }], height, top, light, dark, sort, alpha);
    }
}
/** One upright slab on a world-space quad: side faces sorted back to front, then the cap. */
function prism(out: Container[], quad: Point[], height: number, top: number, light: number, dark: number, sort: 'near' | 'far' | 'center', alpha = 1) {
    const base = quad.map(p => projectWorld(p.x, p.y));
    const centerX = base.reduce((sum, p) => sum + p.x, 0) / 4;
    const g = new Graphics();
    const faces = base.map((p, i) => [p, base[(i + 1) % 4]]).sort(([a1, a2], [b1, b2]) => (a1.y + a2.y) - (b1.y + b2.y));
    for (const [p, q] of faces)
        g.beginFill((p.x + q.x) / 2 < centerX ? dark : light, alpha).drawPolygon([p.x, p.y, q.x, q.y, q.x, q.y - height, p.x, p.y - height]).endFill();
    g.beginFill(top).drawPolygon(base.flatMap(p => [p.x, p.y - height])).endFill();
    const ys = base.map(p => p.y);
    g.zIndex = sort === 'near' ? Math.max(...ys) : sort === 'far' ? Math.min(...ys) : ys.reduce((sum, y) => sum + y, 0) / 4;
    out.push(g);
}
/** A wall along any straight run (slanted runs and flattened curves included), cut into short slabs for depth.
 *  A slab stands tall only when the floor it bounds lies on the camera side; one whose floor lies behind it stays low,
 *  so it never hides that floor. With no outline (or an interior run) the slab's half of the screen decides. */
function wallRun(out: Container[], [x1, y1, x2, y2]: readonly number[], thickness: number, outline?: readonly number[]) {
    const len = Math.hypot(x2 - x1, y2 - y1), nx = -(y2 - y1) / len * thickness / 2, ny = (x2 - x1) / len * thickness / 2;
    const pieces = Math.max(1, Math.ceil(len / 48)), middle = CONFIG.height / 2;
    for (let n = 0; n < pieces; n++) {
        const ax = x1 + (x2 - x1) * n / pieces, ay = y1 + (y2 - y1) * n / pieces, bx = x1 + (x2 - x1) * (n + 1) / pieces, by = y1 + (y2 - y1) * (n + 1) / pieces;
        const back = wallStandsBack((ax + bx) / 2, (ay + by) / 2, nx, ny, outline) ?? projectWorld((ax + bx) / 2, (ay + by) / 2).y < middle;
        prism(out, [{ x: ax + nx, y: ay + ny }, { x: bx + nx, y: by + ny }, { x: bx - nx, y: by - ny }, { x: ax - nx, y: ay - ny }], back ? 112 : 12, 0xf1ffff, 0x6fc3dc, 0x3894b1, back ? 'far' : 'near');
    }
}
/** Whether the floor at a wall point lies down the screen from it (normal (nx, ny) across the wall); undefined when
 *  the outline has floor on both sides or neither. */
function wallStandsBack(x: number, y: number, nx: number, ny: number, outline?: readonly number[]): boolean | undefined {
    if (!outline) return undefined;
    const reach = 40 / Math.hypot(nx, ny);
    const plus = insidePolygon(outline, x + nx * reach, y + ny * reach), minus = insidePolygon(outline, x - nx * reach, y - ny * reach);
    if (plus === minus) return undefined;
    const inward = projectDirection(plus ? nx : -nx, plus ? ny : -ny);
    return inward.y > 0;
}
/** Terminal shell, the landside/airside divider and the jet bridges, for the current camera quarter.
 *  Outer walls on the camera side stay low so they never hide the floor behind them. */
/** Stand markings, jet-bridge doors and hold-room carpets for one gate row (world coordinates). */
export function drawGateFloor(layer: Container, gates: readonly (Point & { door?: Point; facing?: readonly number[]; id?: string | null })[], reach: number) {
    layer.removeChildren().forEach(child => child.destroy({ children: true }));
    gates.forEach((gate, i) => {
        // Drawn as a north-facing gate around its door, then turned to the gate's facing.
        const door = gate.door ?? { x: gate.x + 70, y: 330 }, [fx, fy] = gate.facing ?? [0, -1];
        const g = new Container();
        g.position.set(door.x, door.y);
        g.rotation = Math.atan2(fy, fx) + Math.PI / 2;
        layer.addChild(g);
        shape(g).lineStyle(3, 0xffde72, 0.8).moveTo(0, 0).lineTo(0, -reach).moveTo(-30, -reach).lineTo(30, -reach);
        rounded(g, -60, 20, 120, 100, 12, 0x6a9ca7, 0.18);
        shape(g).beginFill(0x273c78, 0.85).drawRect(-50, -10, 100, 24).endFill();
        shape(g).lineStyle(2, 0xc5d5db).beginFill(0xe6e9dc).drawRoundedRect(-50, -75, 100, 65, 4).endFill();
        for (const y of [-50, -43]) shape(g).lineStyle(2, 0x9eb1b8).moveTo(-47, y).lineTo(47, y);
        shape(g).lineStyle(2, 0xf4ce73).moveTo(-47, -72).lineTo(-47, -14).moveTo(47, -72).lineTo(47, -14);
        const letter = label(g, gate.id ?? String.fromCharCode(65 + i), -7, 88, 16, 0x547b89, '800');
        letter.rotation = -g.rotation;
    });
}
type Rect = { x: number; y: number; w: number; h: number };
/** Floor still behind hoarding: dimmed under construction stripes and labelled with the gate that opens it. */
export function hoardedFloor(areas: readonly { ring: readonly number[]; gate: string; door: Point }[]): Container {
    const c = new Container(), stripes = shape(c), mask = shape(c);
    for (const { ring, gate, door } of areas) {
        const xs = ring.filter((_, i) => i % 2 === 0), ys = ring.filter((_, i) => i % 2 === 1);
        const left = Math.min(...xs), right = Math.max(...xs), top = Math.min(...ys), bottom = Math.max(...ys);
        mask.beginFill(0xffffff).drawPolygon([...ring]).endFill();
        stripes.beginFill(0x55636a, 0.5).drawPolygon([...ring]).endFill();
        stripes.lineStyle(14, 0xf2b84b, 0.45);
        for (let d = left - (bottom - top); d < right; d += 56) stripes.moveTo(d, bottom).lineTo(d + (bottom - top), top);
        stripes.lineStyle(0);
        // Away from the gate, whose door art would stand in front of it.
        const cx = (left + right) / 2, cy = (top + bottom) / 2;
        label(c, `Opens with Gate ${gate}`, cx + (cx - door.x) * 0.45, cy + (cy - door.y) * 0.45, 18, 0xffffff, '800');
    }
    stripes.mask = mask;
    return c;
}
export function walls(boundaries: readonly Rect[] = BOUNDARIES, divider: readonly Rect[] = DIVIDER, runs?: readonly (readonly number[])[], hoarding: readonly Rect[] = [], outline?: readonly number[]): Container[] {
    const out: Container[] = [], middle = CONFIG.height / 2;
    // An outlined airport draws its walls along the outline itself; colliders are only the physics stand-in.
    if (runs) for (const run of runs) wallRun(out, run, 24, outline);
    else for (const r of boundaries) {
        const back = projectWorld(r.x, r.y).y < middle;
        box(out, bounds(r), back ? 112 : 12, 0xf1ffff, 0x6fc3dc, 0x3894b1, back ? 'far' : 'near');
    }
    // The security wall is glazed, so the gates stay visible behind it.
    for (const r of divider) box(out, bounds(r), 64, 0xf4ffff, 0xbfe6f5, 0x8cc3e0, 'center', 0.55);
    // Construction hoarding round an expansion that has not opened yet.
    for (const r of hoarding) box(out, bounds(r), 56, 0xffd98a, 0xf2b84b, 0xc98a2a, 'center');
    return out;
}
/** Screen height of a parked aircraft's door sill: the top of its airstairs. */
export const DOOR_SILL = 45;
/** Screen pixels the airfield sits below a terminal whose gates are on an upper departures deck (jet-bridge airports). */
export const DECK_DROP = 96;
export function deckDrop(airportId: string) { return boardingStyle(airportId) === 'bridge' ? DECK_DROP : 0; }
/** Quarter turns that take a model's turn-0 direction `base` to the world direction `want` (both axis-aligned). */
function quarterTurn(base: readonly number[], want: readonly number[]) {
    let [x, y] = base;
    for (let k = 0; k < 4; k++) { if (Math.abs(x - want[0]) < 0.5 && Math.abs(y - want[1]) < 0.5) return k; [x, y] = [-y, x]; }
    return 0;
}
/** Turn-0 directions of the jet-set models: the bridge tunnel's run, the cab's open mouth, the airstairs' climb. */
const DECK_RUN = [1, 0], HEAD_MOUTH = [0, 1], STAIRS_CLIMB = [-1, 0];
/**
 * Screen pixels per pixel of the 512px jet-set renders; sizes are each view's longest side. The cab is big enough to
 * fill the gate doorway (BRIDGE_HEAD square); the tunnel is narrow, about 26 wide and 77 long a section, sized to the aircraft door.
 */
const HEAD_SCALE = BRIDGE_HEAD / 273, HEAD_SIZE = 387 * HEAD_SCALE, DECK_SCALE = 0.27, DECK_LENGTH = 404 * DECK_SCALE, DECK_SIZE = 381 * DECK_SCALE;
/** The tunnel rides off the apron, level with the aircraft door, on a short pier near its outer end. */
const BRIDGE_LIFT = 14, SUPPORT_SIZE = 420 * BRIDGE_LIFT / 309, STAIRS_SIZE = 108;
/** Jet bridges (the jet-set cab, tunnel and support models) from each bought gate's door out to its parked aircraft. */
export function jetBridges(airportId: string, gates: number): Container[] {
    const out: Container[] = [];
    // Parts below the deck (piers, the tunnel) are marked with their apron point, for the scene's behind-the-deck layer.
    const place = (node: Container, x: number, y: number, depth: number, lift: number) => { const c: Container & { apron?: Point } = new Container(); node.y = -lift; c.addChild(node); standAt(c, x, y, depth); if (lift !== 0) c.apron = { x, y }; out.push(c); };
    const drop = deckDrop(airportId);
    for (let gate = 0; gate < Math.min(gates, gateLayout(airportId).GATES.length); gate++) {
        const b = jetBridge(airportId, gate), len = Math.hypot(b.to.x - b.from.x, b.to.y - b.from.y), ux = (b.to.x - b.from.x) / len, uy = (b.to.y - b.from.y) / len;
        // The cab stands at the departures deck's doorway on a pier down to the apron.
        place(primitive('boarding-bridge-support', 420 * drop / 309), b.head.x, b.head.y, 0, -drop);
        place(primitive('boarding-bridge-head', HEAD_SIZE, quarterTurn(HEAD_MOUTH, b.mouth)), b.head.x, b.head.y, 1, 0);
        // One telescoping tunnel from the cab's far face down to the aircraft door a storey below: its sections nest into
        // one another, and the whole run is sheared straight down the screen so it slopes as a single piece.
        const start = BRIDGE_LIFT - 12, end = DOOR_SILL - drop - 8, o = projectWorld(b.from.x, b.from.y), far = projectWorld(b.to.x, b.to.y);
        const tunnel: Container & { apron?: Point } = new Container(), count = Math.max(1, Math.ceil(len / DECK_LENGTH)), step = count > 1 ? (len - DECK_LENGTH) / (count - 1) : 0;
        const sections = Array.from({ length: count }, (_, n) => { const along = DECK_LENGTH / 2 + step * n; return projectWorld(b.from.x + ux * along, b.from.y + uy * along); });
        for (const at of [...sections].sort((p, q) => p.y - q.y)) {
            const section = primitive('boarding-bridge-deck', DECK_SIZE, quarterTurn(DECK_RUN, b.facing) % 2);
            section.position.set(at.x - o.x, at.y - o.y - start);
            tunnel.addChild(section);
        }
        tunnel.transform.setFromMatrix(new Matrix(1, (start - end) / (far.x - o.x), 0, 1, o.x, o.y));
        const mid = projectWorld((b.from.x + b.to.x) / 2, (b.from.y + b.to.y) / 2);
        tunnel.zIndex = mid.y + 2;
        tunnel.apron = { x: (b.from.x + b.to.x) / 2, y: (b.from.y + b.to.y) / 2 };
        out.push(tunnel);
        // A pier under the tunnel's outer end, up from the apron.
        const t = 1 - DECK_LENGTH / 2 / len, pierLift = start + (end - start) * t;
        place(primitive('boarding-bridge-support', 420 * (pierLift + drop) / 309), b.from.x + ux * len * t, b.from.y + uy * len * t, 1, -drop);
    }
    return out;
}
/** The jet-set airstairs truck, turned to climb toward a parked aircraft's door; stood midway along its flight. */
export function airstairs(airportId: string, gate: number): Container {
    const { at: top, foot } = aircraftDoor(airportId, gate), climb = [Math.sign(Math.round(top.x - foot.x)), Math.sign(Math.round(top.y - foot.y))];
    const c = new Container();
    c.addChild(primitive('passenger-stairs', STAIRS_SIZE, quarterTurn(STAIRS_CLIMB, climb)));
    return c;
}
/** Sprite quarter-turns from a south-facing desk to `facing` (screen view order at the default camera). */
export function facingTurn(facing: readonly number[] | undefined): number {
    const [fx, fy] = facing ?? [0, 1], sx = fx - fy, sy = fx + fy, view = (dx: number, dy: number) => dy < 0 ? dx > 0 ? 3 : 0 : dx > 0 ? 2 : 1;
    return (view(sx, sy) - view(-1, 1) + 4) % 4;
}
export function stationArt(index: number, facing: readonly number[] | undefined = CONFIG.stages[index].facing) {
    const s = CONFIG.stages[index], root = new Container();
    const open = new Container(), closed = new Container();
    root.addChild(open, closed);
    // Origin is the projected counter centre.
    const artIndex = [0, 1, 2, 3, 4, 7, 6, 5][index];
    const sizes = [checkInDeskCellSize, 115, 112, 100, 130, 110, 92, 100];
    const turn = ((index === 2 || index === 5 ? 0 : 2) + facingTurn(facing)) % 4;
    const counter = groundedProp(artIndex, sizes[index], turn);
    open.addChild(counter);
    const title = label(open, s.short, 0, -sizes[index] + 5, 10, C.navy, '800');
    title.anchor.set(0.5);
    const activity = label(open, '', 0, -sizes[index] + 18, 8, C.navy, '700');
    activity.anchor.set(0.5);
    const progress = shape(open);
    progress.y = -sizes[index] + 26;
    const ghost = shape(closed);
    ghost.lineStyle(3, 0xa7bab2, 0.8).beginFill(0xdbe4d8, 0.7).drawPolygon([0, -58, 116, 0, 0, 58, -116, 0]).endFill();
    for (let x = -60; x <= 60; x += 30)
        ghost.lineStyle(7, 0xc8b472, 0.7).moveTo(x - 29, -14 + Math.abs(x) / 2).lineTo(x + 29, 14 - Math.abs(x) / 2);
    label(closed, s.short, 0, -20, 15, 0x7b938a, '800').anchor.set(0.5, 0);
    const closedNote = label(closed, 'UNDER CONSTRUCTION', 0, 2, 10, 0x6f8a80, '700');
    closedNote.anchor.set(0.5, 0);
    const signal = shape(open);
    return { root, open, closed, closedNote, title, activity, progress, counter, signal, hitArea: () => propHitArea(artIndex, sizes[index], turn, true) };
}
export function planeArt() {
    const root = new Container(), image = groundedProp(14, PLANE_SIZE, 2);
    faceAircraft(image, 1, 0);
    root.addChild(image);
    return { root, image };
}

/** Centered native Pixi props. Solid base uses the same rectangle as the native RigidBox. */
export function vendingArt(index: number, _color: number) {
    const root = new Container();
    const image = groundedProp(8, 110, index === 0 ? 0 : 3);
    root.addChild(image);
    const title = label(root, index ? 'DRINKS' : 'SNACKS', 0, -96, 12, C.navy, '800'); title.anchor.set(0.5);
    const status = label(root, '', 0, -83, 9, C.ink, '700'); status.anchor.set(0.5);
    return { root, status, hitArea: () => propHitArea(8, 110, index === 0 ? 0 : 3, true) };
}
export function trashArt(parent: Container, x: number, y: number, kind = 0): Container {
    const root = new Container(); root.position.set(x, y); parent.addChild(root);
    const image = detail(kind === 1 ? 15 : 5, 32);
    image.y = 12;
    root.addChild(image);
    return root;
}
/**
 * The storey under an upper departures deck: the outline's camera-facing edges drop `drop` screen pixels to the
 * airfield as a facade with a glazed arrivals band, drawn in screen space just above the floor layer.
 */
export function deckFacade(outline: readonly number[], drop: number): Graphics {
    const g = new Graphics();
    g.zIndex = -9999;
    if (!drop) return g;
    for (let i = 0; i < outline.length; i += 2) {
        const ax = outline[i], ay = outline[i + 1], bx = outline[(i + 2) % outline.length], by = outline[(i + 3) % outline.length];
        const len = Math.hypot(bx - ax, by - ay);
        if (!len) continue;
        // Outward normal: whichever side of the edge's midpoint lies off the floor.
        let nx = (by - ay) / len, ny = -(bx - ax) / len;
        if (insidePolygon(outline, (ax + bx) / 2 + nx * 3, (ay + by) / 2 + ny * 3)) { nx = -nx; ny = -ny; }
        const facing = projectDirection(nx, ny);
        if (facing.y <= 0) continue;
        const a = projectWorld(ax, ay), b = projectWorld(bx, by), dark = facing.x > 0;
        const band = (top: number, bottom: number, color: number, alpha = 1) =>
            g.beginFill(color, alpha).drawPolygon([a.x, a.y + top, b.x, b.y + top, b.x, b.y + bottom, a.x, a.y + bottom]).endFill();
        // The arrivals storey: a floor slab under the deck, a tall glass curtain wall, and a kerb on the apron.
        band(0, drop, dark ? 0x2f6f8f : 0x4489a8);
        band(0, 16, dark ? 0xd9e7ea : 0xf0f7f8);
        band(16, 20, 0x1f4d66, 0.6);
        band(24, drop - 10, dark ? 0x7cc3dc : 0x9fd8ea, 0.95);
        band(24, 40, 0xffffff, 0.22);
        band(drop - 10, drop, dark ? 0x25546b : 0x346f8a);
        // Mullions and columns every so often along the edge, with a column at each corner.
        const posts = Math.max(1, Math.round(len / 46));
        for (let n = 0; n <= posts; n++) {
            const t = n / posts, x = a.x + (b.x - a.x) * t, y = a.y + (b.y - a.y) * t, column = n % 4 === 0;
            g.lineStyle(column ? 7 : 2.5, column ? (dark ? 0xc9dbe0 : 0xe6f1f3) : 0x2a5d76, 1).moveTo(x, y + 20).lineTo(x, y + drop - 10).lineStyle(0);
        }
        g.lineStyle(2, 0xffffff).moveTo(a.x, a.y).lineTo(b.x, b.y).lineStyle(0);
    }
    return g;
}
/** Whether an airfield point under an upper deck is hidden behind it: straight down the screen from it is the deck. */
export function behindDeck(outline: readonly number[], x: number, y: number): boolean {
    const o = unprojectWorld(0, 0), d = unprojectWorld(0, 1), dx = d.x - o.x, dy = d.y - o.y, k = 1 / Math.hypot(dx, dy);
    for (let t = 20; t < 1400; t += 20) if (insidePolygon(outline, x + dx * k * t, y + dy * k * t)) return true;
    return false;
}
/** Screen-space stencil of everything except the deck, so airfield things behind it never show over its floor. */
export function deckCutout(outline: readonly number[]): Graphics {
    const g = new Graphics(), ring = outline.flatMap((v, i) => i % 2 ? [] : (({ x, y }) => [x, y])(projectWorld(v, outline[i + 1])));
    g.beginFill(0xffffff).drawRect(-20000, -20000, 40000, 40000).beginHole().drawPolygon(ring).endHole().endFill();
    return g;
}
